// Helpers for reading and updating dynamic deal form values.
// They do not contain React/UI logic and do not mutate the original object.

import { PaymentType } from "../../types/types";
import { DealFormData, FormValue } from "../../pages/deals/createDeal/dealFormTypes";

type FormValues = Record<string, FormValue>;

// Some templates use different names for the same field.
const FIELD_ALIAS_GROUPS: string[][] = [
  ["winPayoutType", "winPolicy"],
  ["lossPayoutType", "lossPolicy"],
  ["drawPayoutType", "drawPolicy"],
  ["noShowFaultPolicy", "noShowFaultType"],
  ["noShowMedicalPolicy", "noShowMedicalType"],
  ["eventCancelledPolicy", "eventCancelledType"],
  ["paymentMode", "payment_mode"],
  ["eventDate", "event_date"],
  ["baseAmount", "base_amount"],
];

// Find the matching group and return the other names for this field.
export function getAliasKeys(fieldName: string): string[] {
  for (const aliasGroup of FIELD_ALIAS_GROUPS) {
    if (aliasGroup.includes(fieldName)) {
      return aliasGroup.filter((aliasName) => aliasName !== fieldName);
    }
  }
  return [];
}

export function getAliasedValue(formValues: unknown, fieldNameOrPath: string): FormValue {
  if (!formValues || typeof formValues !== "object") return undefined;

  const values = formValues as FormValues;

  // Read a nested field such as "details.eventDate" one path segment at a time.
  if (fieldNameOrPath.includes(".")) {
    let currentValue: FormValue | undefined = values;
    for (const pathSegment of fieldNameOrPath.split(".")) {
      if (currentValue == null) return undefined;
      currentValue = (currentValue as FormValues)[pathSegment];
    }
    return currentValue;
  }

  if (values[fieldNameOrPath] !== undefined) {
    return values[fieldNameOrPath];
  }

  for (const aliasName of getAliasKeys(fieldNameOrPath)) {
    if (values[aliasName] !== undefined) {
      return values[aliasName];
    }
  }

  return undefined;
}

export function getValueAtPath(
  formData: DealFormData,
  path: string[],
): FormValue | undefined {
  return path.reduce<FormValue | undefined>((currentValue, key) => {
    if (currentValue === null || typeof currentValue !== "object") return undefined;
    if (Array.isArray(currentValue)) return currentValue[Number(key)];
    if (key in currentValue) return currentValue[key];
    return undefined;
  }, formData);
}

// Sets a value at a nested path without changing the original object.
export function setDeep(
  currentObject: FormValue,
  fieldPath: string[],
  newValue: unknown,
): FormValues {
  const [fieldName, ...remainingPathSegments] = fieldPath;
  const objectToUpdate =
    currentObject && typeof currentObject === "object" && !Array.isArray(currentObject)
      ? currentObject
      : {};
  const updatedValue =
    remainingPathSegments.length === 0
      ? newValue
      : setDeep(objectToUpdate[fieldName], remainingPathSegments, newValue);

  return { ...objectToUpdate, [fieldName]: updatedValue as FormValue };
}

// Update the field and its alias (if it has one) at the same nested path.
export function setPathWithAliases(
  formValues: FormValues,
  fieldPath: string[],
  newValue: FormValue,
): FormValues {
  if (fieldPath.length === 0) return newValue as FormValues;

  const parentPath = fieldPath.slice(0, -1);
  const fieldName = fieldPath[fieldPath.length - 1];
  const fieldNamesToUpdate = [fieldName, ...getAliasKeys(fieldName)];
  let updatedFormValues = formValues;

  for (const fieldNameToUpdate of fieldNamesToUpdate) {
    updatedFormValues = setDeep(
      updatedFormValues,
      [...parentPath, fieldNameToUpdate],
      newValue,
    );
  }

  return updatedFormValues;
}

// Backend contract: formData.core.paymentMode is "split" (payments before and
// after the event) or "upfront" (single payment). Anything else = upfront.
export function getPaymentType(
  formValues: FormValues | undefined | null,
): PaymentType {
  const paymentMode = String(
    (formValues?.core as FormValues | undefined)?.paymentMode ?? "",
  )
    .toLowerCase()
    .trim();
  return paymentMode === "split" ? PaymentType.SPLIT : PaymentType.UPFRONT;
}
